<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h1 class="h2 mb-1 fw-bold">FAA Reports</h1>
        <p class="text-muted">Manage FAA compliance reports across all organizations</p>
      </div>
      <div class="col-md-6 text-md-end">
        <button type="button" class="btn btn-outline-secondary" (click)="loadQueueStats(); loadReports()">
          <i class="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>
    </div>

    <!-- Queue Stats Summary -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title mb-3">
              <i class="bi bi-speedometer2 me-2"></i>
              Queue Statistics
            </h6>
            <div class="row g-3">
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-warning">{{ queueStats.pending }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Pending</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-info">{{ queueStats.active }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Active</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-success">{{ queueStats.completed }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Completed</span>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-3">
                <div class="text-center p-2 rounded bg-body-tertiary">
                  <span class="d-block fs-4 fw-semibold lh-sm text-danger">{{ queueStats.failed }}</span>
                  <span class="d-block small text-uppercase text-body-secondary mt-1">Failed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Batch Generation Section -->
    <div class="card mb-4">
      <div class="card-header batch-header cursor-pointer user-select-none" (click)="toggleBatchSection()">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="fw-semibold mb-0">
            <i class="bi bi-collection me-2"></i>
            Generate Batch Reports
          </h6>
          <div class="d-flex align-items-center gap-3">
            <span *ngIf="selectedOrganizations.size > 0" class="badge bg-primary">
              {{ selectedOrganizations.size }} selected
            </span>
            <i class="bi text-body-secondary" [class.bi-chevron-down]="!batchSectionExpanded" [class.bi-chevron-up]="batchSectionExpanded"></i>
          </div>
        </div>
      </div>
      <div class="card-body" *ngIf="batchSectionExpanded">
        <div class="row">
          <!-- Organization Selection Column -->
          <div class="col-lg-6 mb-4 mb-lg-0">
            <h6 class="small fw-semibold text-uppercase mb-3">Select Organizations</h6>

            <!-- Organization Search -->
            <div class="input-group mb-3">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search organizations..."
                [(ngModel)]="batchOrgSearch"
                (input)="applyBatchOrgFilter()"
              />
            </div>

            <!-- Selection Buttons -->
            <div class="d-flex gap-2 mb-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="selectAllFiltered()"
              >
                <i class="bi bi-check-all me-1"></i>
                Select All ({{ filteredOrganizations.length }})
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                (click)="clearSelection()"
                [disabled]="selectedOrganizations.size === 0"
              >
                <i class="bi bi-x-circle me-1"></i>
                Clear Selection
              </button>
            </div>

            <!-- Organization List -->
            <div class="batch-org-list overflow-y-auto border rounded">
              <div class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                  <thead class="sticky-top">
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>Organization</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="filteredOrganizations.length === 0">
                      <td colspan="2" class="text-center text-muted py-3">
                        No organizations found
                      </td>
                    </tr>
                    <tr
                      *ngFor="let org of filteredOrganizations"
                      (click)="toggleOrganizationSelection(org.id)"
                      class="cursor-pointer"
                      [class.table-active]="isOrganizationSelected(org.id)"
                    >
                      <td class="text-center py-2">
                        <input
                          type="checkbox"
                          class="form-check-input cursor-pointer"
                          [checked]="isOrganizationSelected(org.id)"
                          (click)="$event.stopPropagation()"
                          (change)="toggleOrganizationSelection(org.id)"
                        />
                      </td>
                      <td class="py-2">
                        <span class="fw-medium" [class.text-primary]="isOrganizationSelected(org.id)">{{ org.name }}</span>
                        <small class="text-muted d-block">{{ org.id }}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Options Column -->
          <div class="col-lg-6">
            <h6 class="small fw-semibold text-uppercase mb-3">Report Options</h6>

            <!-- Date Range -->
            <div class="row g-3 mb-4">
              <div class="col-6">
                <label class="form-label small text-body-secondary">Start Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="batchStartDate"
                  [class.is-invalid]="batchStartDate && batchEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-6">
                <label class="form-label small text-body-secondary">End Date</label>
                <input
                  type="date"
                  class="form-control"
                  [(ngModel)]="batchEndDate"
                  [class.is-invalid]="batchStartDate && batchEndDate && !isDateRangeValid()"
                />
              </div>
              <div class="col-12" *ngIf="batchStartDate && batchEndDate && !isDateRangeValid()">
                <div class="invalid-feedback d-block">
                  <i class="bi bi-exclamation-circle me-1"></i>
                  End date must be on or after start date
                </div>
              </div>
            </div>

            <!-- Report Content Options -->
            <div class="mb-4">
              <label class="form-label small text-body-secondary">Include in Report</label>
              <div class="row row-cols-2 row-cols-lg-4 g-2">
                <div class="col">
                  <div class="form-check mb-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="batchIncludeFlightLogs"
                      [(ngModel)]="batchOptions.includeFlightLogs"
                    />
                    <label class="form-check-label small cursor-pointer" for="batchIncludeFlightLogs">
                      Flight Logs
                    </label>
                  </div>
                </div>
                <div class="col">
                  <div class="form-check mb-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="batchIncludeSprayRecords"
                      [(ngModel)]="batchOptions.includeSprayRecords"
                    />
                    <label class="form-check-label small cursor-pointer" for="batchIncludeSprayRecords">
                      Spray Records
                    </label>
                  </div>
                </div>
                <div class="col">
                  <div class="form-check mb-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="batchIncludePilotInfo"
                      [(ngModel)]="batchOptions.includePilotInfo"
                    />
                    <label class="form-check-label small cursor-pointer" for="batchIncludePilotInfo">
                      Pilot Info
                    </label>
                  </div>
                </div>
                <div class="col">
                  <div class="form-check mb-0">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="batchIncludeAircraftInfo"
                      [(ngModel)]="batchOptions.includeAircraftInfo"
                    />
                    <label class="form-check-label small cursor-pointer" for="batchIncludeAircraftInfo">
                      Aircraft Info
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Batch Warning -->
            <div *ngIf="showBatchWarning()" class="alert alert-warning mb-4">
              <i class="bi bi-exclamation-triangle me-2"></i>
              You have selected more than 50 organizations. Large batches may take longer to process.
            </div>

            <!-- Generate Button -->
            <div class="d-grid">
              <button
                type="button"
                class="btn btn-primary py-2 fw-medium"
                (click)="confirmBatchGeneration()"
                [disabled]="!canSubmitBatch() || batchGenerating"
              >
                <span *ngIf="batchGenerating" class="spinner-border spinner-border-sm me-2"></span>
                <i *ngIf="!batchGenerating" class="bi bi-file-earmark-plus me-2"></i>
                Generate {{ selectedOrganizations.size }} Report{{ selectedOrganizations.size === 1 ? '' : 's' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters & Search -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <!-- Search -->
          <div class="col-md-4">
            <label class="form-label small text-body-secondary">Search</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control"
                placeholder="Search by report name..."
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
              />
              <button class="btn btn-outline-secondary" type="button" (click)="onSearch()">
                Search
              </button>
            </div>
          </div>

          <!-- Organization Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Organization</label>
            <select class="form-select" [(ngModel)]="filterOrganization" (change)="onFilterChange()">
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
            </select>
          </div>

          <!-- Status Filter -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Status</label>
            <select class="form-select" [(ngModel)]="filterStatus" (change)="onFilterChange()">
              <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
            </select>
          </div>

          <!-- Date Range -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Created From</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterStartDate"
              (change)="onFilterChange()"
            />
          </div>

          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Created To</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterEndDate"
              (change)="onFilterChange()"
            />
          </div>
        </div>

        <!-- Clear Filters Button -->
        <div class="row mt-3" *ngIf="searchQuery || filterOrganization || filterStatus !== 'all' || filterStartDate || filterEndDate">
          <div class="col-12">
            <button type="button" class="btn btn-outline-primary" (click)="clearFilters()">
              <i class="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Reports Table -->
    <div *ngIf="!loading" class="card overflow-hidden">
      <div class="table-responsive">
        <table class="table data-table table-hover mb-0">
          <thead>
            <tr>
              <th>Report Name</th>
              <th>Organization</th>
              <th>Date Range</th>
              <th>Status</th>
              <th>Created</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="reports.length === 0">
              <td colspan="6" class="text-center text-muted py-4">
                No reports found
              </td>
            </tr>
            <tr *ngFor="let report of reports">
              <td>
                <div class="d-flex align-items-center gap-3">
                  <div class="entity-avatar entity-avatar-square">
                    <i class="bi bi-file-earmark-text"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ report.name }}</div>
                    <small class="text-muted">{{ report.reportType === 'complete_faa' ? 'Complete FAA Report' : report.reportType }}</small>
                  </div>
                </div>
              </td>
              <td>
                <span>{{ report.organizationName || getOrganizationName(report.organizationId) }}</span>
              </td>
              <td>
                <small class="text-muted">{{ formatDateRange(report.startDate, report.endDate) }}</small>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusClass(report.status)">
                  <i class="bi" [class]="getStatusIcon(report.status)"></i>
                  {{ getStatusLabel(report.status) }}
                </span>
                <div *ngIf="report.status === 'generating'" class="progress mt-1" style="height: 4px;">
                  <div class="progress-bar progress-bar-striped progress-bar-animated"
                       [style.width.%]="report.generationProgress">
                  </div>
                </div>
              </td>
              <td>
                <small class="text-muted">{{ formatDate(report.createdAt) }}</small>
              </td>
              <td class="text-end">
                <!-- View Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary me-1"
                  (click)="viewReport(report)"
                  title="View Details"
                >
                  <i class="bi bi-eye"></i>
                </button>

                <!-- Retry Button (only for failed) -->
                <button
                  *ngIf="canRetry(report)"
                  type="button"
                  class="btn btn-sm btn-outline-warning me-1"
                  (click)="retryReport(report)"
                  [disabled]="actionLoading"
                  title="Retry Failed Job"
                >
                  <i class="bi bi-arrow-repeat"></i>
                </button>

                <!-- Regenerate Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-info me-1"
                  (click)="confirmRegenerate(report)"
                  [disabled]="actionLoading"
                  title="Regenerate Report"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>

                <!-- Delete Button -->
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  (click)="confirmDelete(report)"
                  [disabled]="!canDelete(report) || actionLoading"
                  [title]="canDelete(report) ? 'Delete Report' : 'Cannot delete while generating'"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer" *ngIf="totalPages > 1">
        <div class="d-flex justify-content-between align-items-center">
          <div class="text-muted small">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalReports) }} of {{ totalReports }} reports
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li
                *ngFor="let page of paginationRange"
                class="page-item"
                [class.active]="page === currentPage"
              >
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Report Detail Modal -->
<div class="modal fade" id="detailModal" tabindex="-1">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-file-earmark-text me-2"></i>Report Details
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="closeDetailModal()"></button>
      </div>
      <div class="modal-body" *ngIf="selectedReport">
        <!-- Report Metadata -->
        <div class="row mb-4">
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Report Information</h6>
            <dl class="row mb-2">
              <dt class="col-5 fw-normal text-body-secondary">Name</dt><dd class="col-7 fw-medium">{{ selectedReport.name }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Organization</dt><dd class="col-7">{{ selectedReport.organizationName || getOrganizationName(selectedReport.organizationId) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Report Type</dt><dd class="col-7">{{ selectedReport.reportType === 'complete_faa' ? 'Complete FAA Report' : selectedReport.reportType }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Date Range</dt><dd class="col-7">{{ formatDateRange(selectedReport.startDate, selectedReport.endDate) }}</dd>
              <dt class="col-5 fw-normal text-body-secondary">Created</dt><dd class="col-7">{{ formatDate(selectedReport.createdAt) }}</dd>
            </dl>
          </div>
          <div class="col-md-6">
            <h6 class="text-muted mb-2">Status</h6>
            <div class="mb-3">
              <span class="badge" [ngClass]="getStatusClass(selectedReport.status)">
                <i class="bi" [class]="getStatusIcon(selectedReport.status)"></i>
                {{ getStatusLabel(selectedReport.status) }}
              </span>
            </div>

            <!-- Progress bar for generating -->
            <div *ngIf="selectedReport.status === 'generating'" class="mb-3">
              <label class="form-label small text-muted">Generation Progress</label>
              <div class="progress" style="height: 20px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
                     [style.width.%]="selectedReport.generationProgress">
                  {{ selectedReport.generationProgress }}%
                </div>
              </div>
            </div>

            <!-- Report stats for completed -->
            <div *ngIf="selectedReport.status === 'ready' || selectedReport.status === 'submitted'">
              <dl class="row mb-2">
                <ng-container *ngIf="selectedReport.recordCount !== null"><dt class="col-5 fw-normal text-body-secondary">Records</dt><dd class="col-7">{{ selectedReport.recordCount }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.fileSize"><dt class="col-5 fw-normal text-body-secondary">File Size</dt><dd class="col-7">{{ selectedReport.fileSize }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedAt"><dt class="col-5 fw-normal text-body-secondary">Submitted</dt><dd class="col-7">{{ formatDate(selectedReport.submittedAt) }}</dd></ng-container>
                <ng-container *ngIf="selectedReport.submittedTo"><dt class="col-5 fw-normal text-body-secondary">Submitted To</dt><dd class="col-7">{{ selectedReport.submittedTo }}</dd></ng-container>
              </dl>
            </div>

            <!-- Error message for failed -->
            <div *ngIf="selectedReport.status === 'failed' && selectedReport.errorMessage" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Error:</strong> {{ selectedReport.errorMessage }}
            </div>
          </div>
        </div>

        <!-- Report Options -->
        <div class="mb-4" *ngIf="selectedReport.options">
          <h6 class="text-muted mb-2">Report Options</h6>
          <div class="row g-2">
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeFlightLogs ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeFlightLogs ? 'bi-check' : 'bi-x'"></i>
                Flight Logs
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeSprayRecords ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeSprayRecords ? 'bi-check' : 'bi-x'"></i>
                Spray Records
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includePilotInfo ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includePilotInfo ? 'bi-check' : 'bi-x'"></i>
                Pilot Info
              </span>
            </div>
            <div class="col-12 col-sm-6 col-lg-3">
              <span class="badge" [ngClass]="selectedReport.options.includeAircraftInfo ? 'bg-success' : 'bg-secondary'">
                <i class="bi" [class]="selectedReport.options.includeAircraftInfo ? 'bi-check' : 'bi-x'"></i>
                Aircraft Info
              </span>
            </div>
          </div>
        </div>

        <!-- Job Information -->
        <div *ngIf="selectedReport.jobs && selectedReport.jobs.length > 0">
          <h6 class="text-muted mb-2">Job History</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Created</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let job of selectedReport.jobs">
                  <td><code class="small bg-body-tertiary rounded px-1">{{ job.id }}</code></td>
                  <td>
                    <span class="badge bg-secondary">{{ job.status }}</span>
                  </td>
                  <td>{{ job.attemptsMade }}</td>
                  <td>{{ formatTimestamp(job?.createdAt ?? job?.timestamp) }}</td>
                  <td>{{ formatTimestamp(job?.finishedAt ?? job?.finishedOn) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div *ngIf="selectedReport.jobs[0]?.failedReason" class="alert alert-danger mt-2">
            <strong>Failure Reason:</strong> {{ selectedReport.jobs[0].failedReason }}
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button
          *ngIf="selectedReport?.reportUrl"
          type="button"
          class="btn btn-primary"
          (click)="openReportUrl()"
        >
          <i class="bi bi-download me-2"></i>
          Download Report
        </button>
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="closeDetailModal()">
          Close
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div class="modal fade" id="deleteModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>
          Confirm Delete
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelDelete()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToDelete">
        <p>Are you sure you want to delete the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToDelete.name }}</strong>
            <br>
            <small class="text-muted">{{ reportToDelete.organizationName || getOrganizationName(reportToDelete.organizationId) }}</small>
          </div>
        </div>
        <div class="alert alert-warning mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          This action cannot be undone. The report will be permanently deleted.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelDelete()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="executeDelete()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Delete Report
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Regenerate Confirmation Modal -->
<div class="modal fade" id="regenerateModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-arrow-clockwise text-info me-2"></i>
          Confirm Regenerate
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelRegenerate()"></button>
      </div>
      <div class="modal-body" *ngIf="reportToRegenerate">
        <p>Are you sure you want to regenerate the following report?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ reportToRegenerate.name }}</strong>
            <br>
            <small class="text-muted">{{ reportToRegenerate.organizationName || getOrganizationName(reportToRegenerate.organizationId) }}</small>
          </div>
        </div>
        <div class="alert alert-info mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          A new generation job will be created. This will overwrite any existing report file.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelRegenerate()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-info"
          (click)="executeRegenerate()"
          [disabled]="actionLoading"
        >
          <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-1"></span>
          Regenerate Report
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Batch Generation Confirmation Modal -->
<div class="modal fade" id="batchConfirmModal" tabindex="-1">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-collection text-primary me-2"></i>
          Confirm Batch Generation
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelBatchConfirmation()"></button>
      </div>
      <div class="modal-body">
        <p>{{ getBatchConfirmationText() }}</p>

        <div class="card mb-3">
          <div class="card-body py-3">
            <div class="row">
              <div class="col-6">
                <small class="text-muted d-block">Organizations</small>
                <strong>{{ selectedOrganizations.size }}</strong>
              </div>
              <div class="col-6">
                <small class="text-muted d-block">Date Range</small>
                <strong>{{ formatDateRange(batchStartDate, batchEndDate) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- FAA §F.2.d missing-count warning. Advisory only: the Generate
             button below is never disabled by it. Renders nothing while
             loading or when the check is unavailable. -->
        <div *ngIf="missingCounts && missingCounts.recordsMissingCount > 0"
             class="alert alert-warning">
          <div class="d-flex">
            <i class="bi bi-exclamation-triangle me-2 mt-1"></i>
            <div>
              <strong>
                {{ missingCounts.recordsMissingCount }} of {{ missingCounts.totalRecords }}
                records have no flight count entered.
              </strong>
              <div class="small mt-1">
                {{ missingCounts.flightsMissingCount }} aircraft will each be filed as
                <strong>1 flight</strong>, which may understate what was actually flown.
                Affects {{ missingCounts.organizations.length }}
                of {{ missingCounts.organizationsChecked }} organizations checked.
              </div>
              <div class="small text-muted mt-1">
                You can generate anyway &mdash; this is a warning, not a block.
              </div>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <small class="text-muted d-block mb-2">Report Options</small>
          <div class="d-flex flex-wrap gap-2">
            <span class="badge" [ngClass]="batchOptions.includeFlightLogs ? 'bg-success' : 'bg-secondary'">
              <i class="bi" [class]="batchOptions.includeFlightLogs ? 'bi-check' : 'bi-x'"></i>
              Flight Logs
            </span>
            <span class="badge" [ngClass]="batchOptions.includeSprayRecords ? 'bg-success' : 'bg-secondary'">
              <i class="bi" [class]="batchOptions.includeSprayRecords ? 'bi-check' : 'bi-x'"></i>
              Spray Records
            </span>
            <span class="badge" [ngClass]="batchOptions.includePilotInfo ? 'bg-success' : 'bg-secondary'">
              <i class="bi" [class]="batchOptions.includePilotInfo ? 'bi-check' : 'bi-x'"></i>
              Pilot Info
            </span>
            <span class="badge" [ngClass]="batchOptions.includeAircraftInfo ? 'bg-success' : 'bg-secondary'">
              <i class="bi" [class]="batchOptions.includeAircraftInfo ? 'bi-check' : 'bi-x'"></i>
              Aircraft Info
            </span>
          </div>
        </div>

        <div *ngIf="showBatchWarning()" class="alert alert-warning mb-0">
          <i class="bi bi-exclamation-triangle me-2"></i>
          Large batch: {{ selectedOrganizations.size }} organizations selected. This may take some time to process.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelBatchConfirmation()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="executeBatchGeneration()"
          [disabled]="batchGenerating"
        >
          <span *ngIf="batchGenerating" class="spinner-border spinner-border-sm me-1"></span>
          Generate Reports
        </button>
      </div>
    </div>
  </div>
</div>
